import React from "react";

export const getOrderItemDisplayPrice = (item, orderType = "") => {
    if (item?.is_complementary || item?.is_loyalty_free || item?.is_offer_free || item?.is_free || Number(item?.calculated_price) === 0) {
        return { isFree: true, price: "0.00", hasExtra: false, extraAmount: 0 };
    }

    if (item?.is_offer_discount) {
        return {
            isOfferDiscount: true,
            originalPrice: Number(item.price || item.base_price || 0).toFixed(2),
            price: Number(item.calculated_price || 0).toFixed(2),
            discountAmount: Number(item.offer_discount_amount || 0).toFixed(2),
            hasExtra: false,
            extraAmount: 0
        };
    }

    const calcPrice = item?.calculated_price != null ? Number(item.calculated_price) : null;
    const basePrice = Number(item?.price ?? item?.base_price ?? 0);
    const hasExtra = calcPrice != null && calcPrice > basePrice && basePrice > 0;
    const extraAmount = hasExtra ? calcPrice - basePrice : 0;

    let finalPriceStr = null;
    if (calcPrice != null && calcPrice > 0) {
        finalPriceStr = calcPrice.toFixed(2);
    } else {
        const isTable = orderType === "in_house" || orderType === "table";
        if (isTable) {
            if (item?.variant?.in_price) finalPriceStr = Number(item.variant.in_price).toFixed(2);
            else if (item?.in_price) finalPriceStr = Number(item.in_price).toFixed(2);
        } else {
            if (item?.variant?.out_price) finalPriceStr = Number(item.variant.out_price).toFixed(2);
            else if (item?.out_price) finalPriceStr = Number(item.out_price).toFixed(2);
        }

        if (!finalPriceStr) {
            if (item?.variant?.price) finalPriceStr = Number(item.variant.price).toFixed(2);
            else if (item?.buffet_price_for === "adult" && item?.adult_price) finalPriceStr = Number(item.adult_price).toFixed(2);
            else if (item?.buffet_price_for === "kids" && item?.kids_price) finalPriceStr = Number(item.kids_price).toFixed(2);
            else if (item?.price) finalPriceStr = Number(item.price).toFixed(2);
            else finalPriceStr = "0.00";
        }
    }

    return {
        isFree: false,
        isOfferDiscount: false,
        price: finalPriceStr,
        hasExtra,
        extraAmount
    };
};

export const OrderItemPriceDisplay = ({ item, orderType = "" }) => {
    const priceInfo = getOrderItemDisplayPrice(item, orderType);

    if (priceInfo.isFree) {
        return (
            <div className="flex items-center justify-end grow text-right">
                <span className="text-green-500 font-semibold text-end">(Free)</span>
            </div>
        );
    }

    if (priceInfo.isOfferDiscount) {
        return (
            <div className="grow text-right flex flex-col items-end justify-center">
                <span className="text-color4/60 line-through text-[11px] font-medium leading-none">
                    £{priceInfo.originalPrice}
                </span>
                <span className="text-color6 font-semibold text-sm leading-tight">
                    £{priceInfo.price}
                </span>
                <span className="text-[10px] text-emerald-400 font-medium mt-0.5 leading-none">
                    🏷️ Discount (-£{priceInfo.discountAmount})
                </span>
            </div>
        );
    }

    return (
        <div className="grow text-right flex flex-col items-end justify-center">
            <div className="flex items-center gap-1 justify-end flex-wrap">
                <span className="text-color6 font-semibold text-sm">
                    £{priceInfo.price}
                </span>
                {priceInfo.hasExtra && (
                    <span className="text-[9px] bg-amber-500/20 text-amber-300 border border-amber-500/40 px-1 py-0.2 rounded font-medium shrink-0">
                        +£{priceInfo.extraAmount.toFixed(2)}
                    </span>
                )}
            </div>
        </div>
    );
};

export const getCustomizationList = (target) => {
    if (!target) return [];
    const list = [];

    const addList = (items, isExtra, defaultCategory) => {
        if (!Array.isArray(items)) return;
        items.forEach((elem, idx) => {
            if (!elem) return;
            let name = typeof elem === "string" ? elem : (elem.name || elem.item_name || elem.addon?.name || elem.topping?.name || elem.sauce?.name || elem.salad?.name || elem.modifier?.name || elem.data?.modifier?.name || "");
            const preFix = elem.preFix || elem.data?.preFix || "";
            if (preFix) {
                name = `${preFix} ${name}`;
            }
            if (!name) return;

            const isCanceled = Boolean(elem.canceled === true || elem.is_canceled === true || elem.status === "canceled");
            const price = Number(elem.price || elem.calculated_price || elem.addon?.price || elem.topping?.price || elem.sauce?.price || elem.salad?.price || 0);

            list.push({
                id: elem.id || `${defaultCategory}-${idx}`,
                name,
                isExtra,
                isCanceled,
                price,
                category: defaultCategory,
            });
        });
    };

    // Custom Pizza attributes (Base, Crust)
    const customPizza = target.custom_pizza || target.data?.item?.custom_pizza || null;
    const resolvedBase = (customPizza?.base_name || customPizza?.base_id) ? {
        id: customPizza.base_id,
        name: customPizza.base_name,
        price: Number(customPizza.base_price || 0)
    } : (target.base || target.pizza_base || target.customPizzaData?.base || target.data?.item?.base || null);

    const resolvedCrust = (customPizza?.crust_name || customPizza?.crust_id) ? {
        id: customPizza.crust_id,
        name: customPizza.crust_name,
        price: Number(customPizza.crust_price || 0)
    } : (target.crust || target.pizza_crust || target.customPizzaData?.crust || target.data?.item?.crust || null);

    if (resolvedBase) {
        const baseName = typeof resolvedBase === "string" ? resolvedBase : resolvedBase.name;
        const basePrice = typeof resolvedBase === "object" ? Number(resolvedBase.price || 0) : 0;
        if (baseName) {
            list.push({
                id: (typeof resolvedBase === "object" && resolvedBase.id) ? `base-${resolvedBase.id}` : "base-default",
                name: `Base: ${baseName}`,
                isExtra: basePrice > 0,
                isCanceled: false,
                price: basePrice,
                category: "Base",
            });
        }
    }

    if (resolvedCrust) {
        const crustName = typeof resolvedCrust === "string" ? resolvedCrust : resolvedCrust.name;
        const crustPrice = typeof resolvedCrust === "object" ? Number(resolvedCrust.price || 0) : 0;
        if (crustName) {
            list.push({
                id: (typeof resolvedCrust === "object" && resolvedCrust.id) ? `crust-${resolvedCrust.id}` : "crust-default",
                name: `Crust: ${crustName}`,
                isExtra: crustPrice > 0,
                isCanceled: false,
                price: crustPrice,
                category: "Crust",
            });
        }
    }

    // Default customizations (NO price displayed)
    addList(target.defaultAddons || target.default_addons || target.add_ons || target.addons, false, "Addon");
    addList(target.defaultSauces || target.default_sauces || target.sauces, false, "Sauce");
    addList(target.defaultToppings || target.default_toppings || target.toppings, false, "Topping");
    addList(target.defaultSalads || target.default_salads || target.salads, false, "Salad");
    addList(target.modifiers, false, "Modifier");
    addList(target.banquet_selected_items, false, "Banquet");

    // Extra customizations (Price displayed)
    addList(target.extra_addons || target.extraAddon, true, "Extra Addon");
    addList(target.extra_sauces || target.extraSauces, true, "Extra Sauce");
    addList(target.extra_toppings || target.extraToppings, true, "Extra Topping");
    addList(target.extra_salads || target.extraSalads, true, "Extra Salad");
    addList(target.banquet_extra_items, true, "Extra Banquet");

    return list;
};

export const CustomizationTag = ({ cust }) => {
    if (!cust) return null;
    const { name, isExtra, isCanceled, price } = cust;
    const priceStr = isExtra && price > 0 ? `(+£${Number(price).toFixed(2)})` : "";

    if (isCanceled) {
        return (
            <span
                className="line-through text-gray-400 decoration-red-500/80 decoration-1"
                title="Canceled"
            >
                + {name} {priceStr}
            </span>
        );
    }

    return (
        <span className={isExtra ? "text-amber-300 font-normal" : "text-gray-300"}>
            + {name} {priceStr}
        </span>
    );
};

export const OrderItemCustomizationBreakdown = ({ item, isTabletView = false }) => {
    if (!item) return null;
    const customizations = getCustomizationList(item);
    if (!customizations || customizations.length === 0) return null;

    return (
        <div className={`w-full ${isTabletView ? "pl-2 text-[10px]" : "pl-3 text-[11px]"} py-1 flex flex-wrap gap-x-2 gap-y-1 bg-color3/30 border-l-2 border-color6/40 rounded-r`}>
            {customizations.map((cust, idx) => (
                <CustomizationTag key={cust.id || idx} cust={cust} />
            ))}
        </div>
    );
};

export const OrderItemComboBreakdown = ({ comboItems, isTabletView = false }) => {
    if (!Array.isArray(comboItems) || comboItems.length === 0) return null;

    return (
        <div className={`w-full mt-1 mb-1.5 ${isTabletView ? "pl-3 text-[10px]" : "pl-4 text-[11px]"} pr-1.5 py-1.5 flex flex-col gap-1 bg-color2/70 border-l-2 border-amber-500/70 rounded-r-md`}>
            <div className="flex items-center justify-between font-semibold text-gray-300">
                <span className="flex items-center gap-1 text-amber-400">
                    <span>🍽️ Combo Items ({comboItems.length})</span>
                </span>
            </div>
            <div className="flex flex-col gap-1">
                {comboItems.map((ci, cIdx) => {
                    const ciName = ci.name || ci.display_name || ci.item_name || ci.menu_item?.name || (ci.item_type ? ci.item_type.replace(/_/g, " ") : "Item");
                    const isReplaced = Boolean(ci.is_replaced);
                    const activeExtra = Number(ci.price_difference ?? ci.replacement_extra_out_price ?? ci.replacement_extra_price ?? 0);
                    const customizations = getCustomizationList(ci);

                    return (
                        <div
                            key={ci.id || ci.combo_item_unique_id || cIdx}
                            className="flex flex-col bg-color3/40 rounded px-2 py-1 border border-color3/60 gap-0.5"
                        >
                            <div className="flex items-center justify-between gap-1 flex-wrap">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="text-white font-medium">• {ciName}</span>
                                    {ci.variant?.name && (
                                        <span className="text-color6 font-normal">({ci.variant.name})</span>
                                    )}
                                    <span className="text-color6">x{ci.quantity || 1}</span>
                                    {isReplaced && (
                                        <span className="text-[9px] bg-amber-500/20 text-amber-300 border border-amber-500/40 px-1 py-0.2 rounded font-semibold shrink-0">
                                            Replaced {activeExtra > 0 ? `(+£${activeExtra.toFixed(2)})` : ""}
                                        </span>
                                    )}
                                </div>
                                {isReplaced && activeExtra > 0 && (
                                    <span className="text-amber-400 font-semibold shrink-0">
                                        +£{activeExtra.toFixed(2)}
                                    </span>
                                )}
                            </div>
                            {isReplaced && ci.original_item && (
                                <span className="text-[10px] text-gray-400 italic pl-2">
                                    (Default: {ci.original_item.display_name || ci.original_item.name}{ci.original_item.variant?.name ? ` - ${ci.original_item.variant.name}` : ""})
                                </span>
                            )}
                            {customizations.length > 0 && (
                                <div className="pl-2 flex flex-wrap gap-x-2 gap-y-0.5 text-[10px]">
                                    {customizations.map((cust, idx) => (
                                        <CustomizationTag key={cust.id || idx} cust={cust} />
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export const getNormalizedSelectedCourses = (item, courseItems) => {
    if (!item && !courseItems) return [];

    // 1. Direct course_items array
    const rawCourseItems = courseItems || item?.course_items;
    if (Array.isArray(rawCourseItems) && rawCourseItems.length > 0) {
        return [{
            id: "default",
            name: "Course Items",
            items: rawCourseItems
        }];
    }

    // 2. Pre-formatted selectedCourseItem on cart/order item
    if (Array.isArray(item?.selectedCourseItem) && item.selectedCourseItem.length > 0) {
        return item.selectedCourseItem.map((course, idx) => ({
            id: course.id || idx,
            name: course.name || `Course ${idx + 1}`,
            description: course.description || "",
            items: Array.isArray(course.course_items) ? course.course_items : (Array.isArray(course.items) ? course.items : [])
        })).filter(c => c.items.length > 0);
    }

    const allCourses = item?.create_your_own_general?.courses || item?.banquet?.courses || item?.courses || [];

    // 2b. Course-grouped selectedCreateYourOwnGeneralCourseItem
    if (Array.isArray(item?.selectedCreateYourOwnGeneralCourseItem) && item.selectedCreateYourOwnGeneralCourseItem.length > 0) {
        const isGrouped = item.selectedCreateYourOwnGeneralCourseItem.some(c => Array.isArray(c?.items) || Array.isArray(c?.course_items));
        if (isGrouped && (!Array.isArray(item?.create_your_own_general_selected_items) || item.create_your_own_general_selected_items.length === 0)) {
            const courseMap = new Map();
            if (Array.isArray(allCourses)) {
                allCourses.forEach(c => {
                    courseMap.set(String(c.id), c);
                });
            }

            return item.selectedCreateYourOwnGeneralCourseItem.map((courseGroup, idx) => {
                const cId = String(courseGroup.id || idx);
                const matchedCourse = courseMap.get(cId) || {};
                const courseName = courseGroup.name || courseGroup.course_name || matchedCourse.name || `Course ${idx + 1}`;
                const courseDesc = courseGroup.description || matchedCourse.description || "";
                const groupItems = Array.isArray(courseGroup.items) ? courseGroup.items : (Array.isArray(courseGroup.course_items) ? courseGroup.course_items : []);

                return {
                    id: courseGroup.id || idx,
                    name: courseName,
                    description: courseDesc,
                    items: groupItems.map((ci, sIdx) => {
                        const itemName = ci.display_name || ci.name || ci.item_name || ci.menu_item?.name || "Item";
                        let variantObj = null;
                        if (ci.variant && typeof ci.variant === "object") {
                            variantObj = ci.variant;
                        } else if (ci.variant_name) {
                            variantObj = { id: ci.variant || ci.variant_id, name: ci.variant_name };
                        } else if (ci.variant != null && typeof ci.variant === "string") {
                            variantObj = { id: ci.variant, name: ci.variant };
                        }

                        return {
                            id: ci.id || `sel-${idx}-${sIdx}`,
                            name: itemName,
                            display_name: itemName,
                            variant: variantObj,
                            quantity: Number(ci.quantity || 1),
                            price: Number(ci.price || 0),
                            item_type: ci.item_type || null,
                            menu_item: ci.menu_item || null,
                            file_manager_url: ci.file_manager_url || ci.menu_item?.file_manager_url || null,
                            is_replaced: Boolean(ci.is_replaced),
                            original_item: ci.original_item,
                            price_difference: ci.price_difference,
                            add_ons: ci.add_ons || ci.addons,
                            extra_addons: ci.extra_addons || ci.extraAddon,
                            extraAddon: ci.extraAddon,
                            sauces: ci.sauces,
                            extra_sauces: ci.extra_sauces || ci.extraSauces,
                            extraSauces: ci.extraSauces,
                            toppings: ci.toppings,
                            extra_toppings: ci.extra_toppings || ci.extraToppings,
                            extraToppings: ci.extraToppings,
                            salads: ci.salads,
                            extra_salads: ci.extra_salads || ci.extraSalads,
                            extraSalads: ci.extraSalads,
                            modifiers: ci.modifiers,
                            defaultAddons: ci.defaultAddons,
                            defaultSauces: ci.defaultSauces,
                            defaultToppings: ci.defaultToppings,
                            defaultSalads: ci.defaultSalads,
                        };
                    })
                };
            }).filter(c => c.items.length > 0);
        }
    }
    const rawSelections = (Array.isArray(item?.selected_course_item) && item.selected_course_item.length > 0)
        ? item.selected_course_item
        : ((Array.isArray(item?.create_your_own_general_selected_items) && item.create_your_own_general_selected_items.length > 0)
            ? item.create_your_own_general_selected_items
            : ((Array.isArray(item?.selectedCreateYourOwnGeneralCourseItem) && item.selectedCreateYourOwnGeneralCourseItem.length > 0)
                ? item.selectedCreateYourOwnGeneralCourseItem
                : ((Array.isArray(item?.selected_create_your_own_general_course_items) && item.selected_create_your_own_general_course_items.length > 0)
                    ? item.selected_create_your_own_general_course_items
                    : ((Array.isArray(item?.selectedBanquetCourseItem) && item.selectedBanquetCourseItem.length > 0)
                        ? item.selectedBanquetCourseItem
                        : ((Array.isArray(item?.banquet_selected_items) && item.banquet_selected_items.length > 0)
                            ? item.banquet_selected_items
                            : [])))));

    // If explicit selections exist, group by course
    if (rawSelections.length > 0) {
        const courseMap = new Map();
        if (Array.isArray(allCourses) && allCourses.length > 0) {
            allCourses.forEach(c => {
                courseMap.set(String(c.id), {
                    id: c.id,
                    name: c.name || "Course",
                    description: c.description || "",
                    items: []
                });
            });
        }

        rawSelections.forEach((bItem, idx) => {
            const courseId = String(bItem.create_your_own_general_course_id || bItem.banquet_course_id || bItem.course_id || bItem.course?.id || "default");
            if (!courseMap.has(courseId)) {
                const courseName = bItem.create_your_own_general_course?.name
                    || bItem.banquet_course?.name 
                    || bItem.course?.name 
                    || bItem.course_name 
                    || bItem.banquet_course_name 
                    || `Course ${courseMap.size + 1}`;

                courseMap.set(courseId, {
                    id: courseId,
                    name: courseName,
                    description: "",
                    items: []
                });
            }
            const courseObj = courseMap.get(courseId);
            const itemName = bItem.display_name || bItem.name || bItem.menu_item?.name || "Item";
            courseObj.items.push({
                id: bItem.id || `sel-${idx}`,
                name: itemName,
                display_name: itemName,
                variant: bItem.variant || (bItem.variant_id ? { id: bItem.variant_id, name: bItem.variant_name || "" } : null),
                quantity: Number(bItem.quantity || 1),
                price: Number(bItem.price || 0),
                item_type: bItem.item_type || null,
                menu_item: bItem.menu_item || null,
                file_manager_url: bItem.file_manager_url || bItem.menu_item?.file_manager_url || null,
                is_replaced: bItem.is_replaced,
                original_item: bItem.original_item,
                price_difference: bItem.price_difference,
                add_ons: bItem.add_ons || bItem.addons,
                extra_addons: bItem.extra_addons,
                extraAddon: bItem.extraAddon,
                sauces: bItem.sauces,
                extra_sauces: bItem.extra_sauces,
                extraSauces: bItem.extraSauces,
                toppings: bItem.toppings,
                extra_toppings: bItem.extra_toppings,
                extraToppings: bItem.extraToppings,
                salads: bItem.salads,
                extra_salads: bItem.extra_salads,
                extraSalads: bItem.extraSalads,
                modifiers: bItem.modifiers,
                defaultAddons: bItem.defaultAddons,
                defaultSauces: bItem.defaultSauces,
                defaultToppings: bItem.defaultToppings,
                defaultSalads: bItem.defaultSalads,
            });
        });

        return Array.from(courseMap.values()).filter(c => c.items.length > 0);
    }

    // Default selection fallback if no explicit selection records but courses exist
    if (Array.isArray(allCourses) && allCourses.length > 0) {
        return allCourses.map((course, idx) => {
            const courseItemsList = Array.isArray(course.course_items) ? course.course_items : [];
            const maxSelect = course.max_selections || 1;
            const defaultSelected = courseItemsList.slice(0, maxSelect);

            return {
                id: course.id || idx,
                name: course.name || `Course ${idx + 1}`,
                description: course.description || "",
                items: defaultSelected.map(ci => ({
                    id: ci.id || ci.item_id,
                    name: ci.display_name || ci.name || ci.menu_item?.name || "Item",
                    display_name: ci.display_name || ci.name || ci.menu_item?.name || "Item",
                    variant: ci.variant || null,
                    quantity: Number(ci.quantity || 1),
                    price: Number(ci.price || 0),
                    file_manager_url: ci.file_manager_url || ci.menu_item?.file_manager_url || null,
                    menu_item: ci.menu_item || null
                }))
            };
        }).filter(c => c.items.length > 0);
    }

    return [];
};

export const OrderItemCourseBreakdown = ({ courseItems, item, isTabletView = false }) => {
    const courses = getNormalizedSelectedCourses(item, courseItems);
    if (!courses || courses.length === 0) return null;

    const totalSelectedCount = courses.reduce((acc, c) => acc + (c.items?.length || 0), 0);

    return (
        <div className={`w-full mt-1 mb-1.5 ${isTabletView ? "pl-3 text-[10px]" : "pl-4 text-[11px]"} pr-1.5 py-1.5 flex flex-col gap-1.5 bg-color2/70 border-l-2 border-emerald-500/70 rounded-r-md`}>
            <div className="flex items-center justify-between font-semibold text-gray-300">
                <span className="flex items-center gap-1 text-emerald-400">
                    <span>🍱 Selected Course Items ({totalSelectedCount})</span>
                </span>
                {item?.create_your_own_general?.description && (
                    <span className="text-[10px] text-gray-400 italic">
                        {item.create_your_own_general.description}
                    </span>
                )}
            </div>
            <div className="flex flex-col gap-1">
                {courses.map((course, cIdx) => (
                    <div key={course.id || cIdx} className="flex flex-col bg-color3/40 rounded px-2 py-1 border border-color3/60 gap-0.5">
                        {courses.length > 1 && (
                            <span className="text-emerald-300 text-[10px] font-semibold">
                                • {course.name}
                            </span>
                        )}
                        <div className="flex flex-col gap-0.5">
                            {course.items.map((ci, sIdx) => {
                                const ciName = ci.name || ci.display_name || ci.item_name || ci.menu_item?.name || "Item";
                                const isReplaced = Boolean(ci.is_replaced);
                                const activeExtra = Number(ci.price_difference ?? ci.replacement_extra_out_price ?? ci.replacement_extra_price ?? 0);
                                const customizations = getCustomizationList(ci);

                                return (
                                    <div key={ci.id || sIdx} className="flex flex-col">
                                        <div className="flex items-center justify-between gap-1 flex-wrap text-gray-200">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className="text-white font-medium">• {ciName}</span>
                                                {ci.variant?.name && !ciName.toLowerCase().includes(ci.variant.name.toLowerCase()) && (
                                                    <span className="text-color6 font-normal">({ci.variant.name})</span>
                                                )}
                                                <span className="text-color6">x{ci.quantity || 1}</span>
                                                {isReplaced && (
                                                    <span className="text-[9px] bg-amber-500/20 text-amber-300 border border-amber-500/40 px-1 py-0.2 rounded font-semibold shrink-0">
                                                        Replaced {activeExtra > 0 ? `(+£${activeExtra.toFixed(2)})` : ""}
                                                    </span>
                                                )}
                                            </div>
                                            {isReplaced && activeExtra > 0 && (
                                                <span className="text-amber-400 font-semibold shrink-0">
                                                    +£{activeExtra.toFixed(2)}
                                                </span>
                                            )}
                                        </div>
                                        {isReplaced && ci.original_item && (
                                            <span className="text-[10px] text-gray-400 italic pl-2">
                                                (Default: {ci.original_item.display_name || ci.original_item.name}{ci.original_item.variant?.name ? ` - ${ci.original_item.variant.name}` : ""})
                                            </span>
                                        )}
                                        {customizations.length > 0 && (
                                            <div className="pl-2 flex flex-wrap gap-x-2 gap-y-0.5 text-[10px]">
                                                {customizations.map((cust, idx) => (
                                                    <CustomizationTag key={cust.id || idx} cust={cust} />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}

                {/* Banquet Extra Items */}
                {Array.isArray(item?.banquet_extra_items) && item.banquet_extra_items.length > 0 && (
                    <div className="flex flex-col bg-color3/40 rounded px-2 py-1 border border-amber-500/40 gap-0.5">
                        <span className="text-amber-400 text-[10px] font-semibold">
                            • Extra Items ({item.banquet_extra_items.length})
                        </span>
                        <div className="flex flex-col gap-0.5">
                            {item.banquet_extra_items.map((extra, eIdx) => {
                                const extraName = extra.display_name || extra.name || extra.menu_item?.name || "Extra Item";
                                const extraCharge = Number(extra.extra_charge ?? extra.price ?? 0);
                                return (
                                    <div key={extra.id || eIdx} className="flex items-center justify-between text-gray-200">
                                        <span className="text-white font-medium">• {extraName} x{extra.quantity || 1}</span>
                                        {extraCharge > 0 && (
                                            <span className="text-amber-400 font-semibold">+£{extraCharge.toFixed(2)}</span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export const OrderItemMixAndMatchBreakdown = ({ items, isTabletView = false }) => {
    if (!Array.isArray(items) || items.length === 0) return null;

    const normalizedItems = [];
    items.forEach((entry, eIdx) => {
        if (Array.isArray(entry?.items)) {
            entry.items.forEach((sub, sIdx) => {
                normalizedItems.push({
                    id: sub.id || sub.item_id || `${eIdx}-${sIdx}`,
                    name: sub.name || sub.item_name || sub.display_name || "Item",
                    category_name: entry.category_name || "",
                    category_price: entry.price ?? entry.category_price,
                    quantity: Number(sub.quantity || 1)
                });
            });
        } else if (entry) {
            normalizedItems.push({
                id: entry.item_id || entry.id || eIdx,
                name: entry.item_name || entry.name || entry.display_name || "Item",
                category_name: entry.category_name || "",
                category_price: entry.category_price ?? entry.price,
                quantity: Number(entry.quantity || 1)
            });
        }
    });

    if (normalizedItems.length === 0) return null;

    return (
        <div className={`w-full mt-1 mb-1.5 ${isTabletView ? "pl-3 text-[10px]" : "pl-4 text-[11px]"} pr-1.5 py-1.5 flex flex-col gap-1 bg-color2/70 border-l-2 border-purple-500/70 rounded-r-md`}>
            <div className="flex items-center justify-between font-semibold text-gray-300">
                <span className="flex items-center gap-1 text-purple-400">
                    <span>🔀 Mix & Match Selections ({normalizedItems.length})</span>
                </span>
            </div>
            <div className="flex flex-col gap-1">
                {normalizedItems.map((mi, mIdx) => {
                    const itemName = mi.name || "Item";
                    const categoryName = mi.category_name || "";
                    const categoryPrice = mi.category_price != null ? Number(mi.category_price) : null;
                    const qty = mi.quantity || 1;

                    return (
                        <div
                            key={mi.id || mIdx}
                            className="flex items-center justify-between bg-color3/40 rounded px-2 py-1 border border-color3/60 gap-1 text-xs"
                        >
                            <div className="flex flex-col">
                                <span className="text-white font-medium">• {itemName}</span>
                                {categoryName && (
                                    <span className="text-color4 text-[10px]">
                                        {categoryName} {categoryPrice != null ? `(£${categoryPrice.toFixed(2)})` : ""}
                                    </span>
                                )}
                            </div>
                            <span className="text-color6 font-semibold">x{qty}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export const OrderItemSpecialMenuBreakdown = ({ specialMenu, item, isTabletView = false }) => {
    const targetMenu = specialMenu || item?.special_menu;
    if (!targetMenu) return null;

    const menuItems = Array.isArray(targetMenu.menu_items) ? targetMenu.menu_items : [];
    const ingredients = Array.isArray(targetMenu.ingredients) ? targetMenu.ingredients : [];
    const description = targetMenu.description || null;

    if (menuItems.length === 0 && ingredients.length === 0 && !description) return null;

    return (
        <div className={`w-full mt-1 mb-1.5 ${isTabletView ? "pl-3 text-[10px]" : "pl-4 text-[11px]"} pr-1.5 py-1.5 flex flex-col gap-1 bg-color2/70 border-l-2 border-amber-400/70 rounded-r-md`}>
            <div className="flex items-center justify-between font-semibold text-gray-300">
                <span className="flex items-center gap-1 text-amber-400">
                    <span>✨ Special Menu Items ({menuItems.length})</span>
                </span>
                {description && (
                    <span className="text-[10px] text-gray-400 italic">
                        {description}
                    </span>
                )}
            </div>

            {menuItems.length > 0 && (
                <div className="flex flex-col gap-1">
                    {menuItems.map((mi, mIdx) => {
                        const miName = mi.display_name || mi.name || mi.item_name || mi.menu_item?.name || "Item";
                        const variantName = mi.variant?.name || mi.variant_name || null;
                        const qty = mi.quantity || 1;
                        const customizations = getCustomizationList(mi);

                        return (
                            <div
                                key={mi.id || mIdx}
                                className="flex flex-col bg-color3/40 rounded px-2 py-1 border border-color3/60 gap-0.5"
                            >
                                <div className="flex items-center justify-between gap-1 flex-wrap">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="text-white font-medium">• {miName}</span>
                                        {variantName && !miName.includes(`(${variantName})`) && (
                                            <span className="text-color6 font-normal">({variantName})</span>
                                        )}
                                        <span className="text-color6">x{qty}</span>
                                    </div>
                                </div>
                                {customizations.length > 0 && (
                                    <div className="pl-2 flex flex-wrap gap-x-2 gap-y-0.5 text-[10px]">
                                        {customizations.map((cust, idx) => (
                                            <CustomizationTag key={cust.id || idx} cust={cust} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {ingredients.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-0.5">
                    {ingredients.map((ing, iIdx) => {
                        const ingName = typeof ing === "string" ? ing : (ing.name || ing.ingredient_name || "");
                        if (!ingName) return null;
                        return (
                            <span key={iIdx} className="px-1.5 py-0.5 rounded bg-color3 text-gray-300 text-[10px]">
                                • {ingName}
                            </span>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default OrderItemComboBreakdown;

